/**
 * TURNI DI LAVORO — la pagina che in Lares è `/erp-work-shifts`.
 *
 * ## Perché nasce, visto che `/admin/working-hours` esisteva
 *
 * Quella pagina ha otto schede e **non parlano tutte dello stesso mestiere**: «Sedi» e
 * «Dipendenti» sono voci di menu a sé, e «Preset orari» e «Regole turnazione» riguardano
 * l'orario settimanale di una persona, non la turnazione ciclica. Chi apre «Turni di lavoro»
 * cercando i turni trova metà barra che parla d'altro, e la voce che gli serve è la terza.
 *
 * Qui ce ne sono quattro, tutte sullo stesso lavoro, nell'ordine in cui lo si fa.
 * `/admin/working-hours` è restata in piedi come metro di paragone finché non è stata validata
 * questa, ed è stata tolta il 12-10-2026 — indirizzo compreso, dal 15-10-2026.
 *
 * ## ⚠️ Quattro schede senza raggruppamenti
 *
 * C'erano due blocchi, «Come si costruisce» e «Cosa ne esce». Su diciotto schede un confine
 * aiuta; su quattro è una riga in più da leggere per capire una cosa che si vede già — l'ordine
 * delle schede **è** l'ordine del lavoro. I blocchi restano in `MaeTabs` per chi ne ha tante.
 *
 * La cosa che invece va detta a parole è la **generazione**: il server non materializza niente
 * su una lettura, ma lo fa da sé quando si salva un'assegnazione, un orario o una regola (fino a
 * novanta giorni, saltando le giornate già scritte), e ogni notte. «Genera» serve per andare oltre
 * e per riscrivere: sta scritto nella scheda «Assegnazioni», sopra il bottone che lo fa.
 * (Fino al 26-10-2026 qui e a schermo si diceva che assegnare non scrive niente: era falso.)
 *
 * ## Due modalità sugli stessi dati — 10-10-2026
 *
 * **Semplificata** (di partenza): due schede, «Calendario» e «Orari e schemi». È la porta per chi
 * pianifica senza essere del mestiere — si lavora sul calendario, e orari e cicli sono la
 * manutenzione. **Avanzata**: le cinque schede di sempre, come in Lares. Tabelle e motore sono gli
 * stessi, quindi si passa dall'una all'altra senza convertire niente. La scelta è della persona
 * (`STORAGE_KEYS.shiftsMode`): ai turni mette mano di solito una persona o un ufficio per azienda,
 * e una scelta a livello d'azienda non servirebbe a nessuno.
 *
 * Il «Calendario» della semplificata è la **settimana che si scrive** (`WeekPlannerPanel`): si
 * scelgono le caselle e ci si mette un turno o un orario, sempre dopo un riassunto da confermare. Il
 * mese di sempre resta a un clic.
 *
 * ## ⚠️ Le schede visitate restano montate
 *
 * Il giro vero è: turni → turnazioni → genera → calendario → *torna indietro a correggere* →
 * rigenera. Smontando una scheda al cambio si perderebbero mese scelto, filtri e ricerca
 * proprio in quel «torna indietro», che è il momento in cui servono. Lares monta le schede a
 * richiesta e **non le smonta più** (`erp-work-shifts.js`, `_mountedTabs`); qui si fa lo
 * stesso, nascondendole con `display`.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Button, Group, SegmentedControl, Stack } from '@mantine/core';
import {
  IconCalendar, IconCalendarRepeat, IconCalendarWeek, IconClock, IconRefresh, IconWand,
} from '@tabler/icons-react';
import { MaeShell, MaePageHead, MaeTabs, type MaeTabDef } from '@/shared/desktop/MaeShell';
import { ShiftsPanel } from '@/features/working-hours/ShiftsPanel';
import { RotationsPanel, RotationAssignmentsPanel } from '@/features/working-hours/RotationsPanel';
import { ShiftCalendarPanel } from '@/features/working-hours/ShiftCalendarPanel';
import { SimpleCalendarPanel } from '@/features/working-hours/WeekPlannerPanel';
import { ShiftGuidePanel } from '@/features/working-hours/ShiftGuidePanel';
import { useMissingRotations } from '@/features/working-hours/rotationCoverage';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { useUserPreference } from '@/shared/lib/usePreferenzaUtente';
import { STORAGE_KEYS } from '@/shared/lib/constants';

type Tab = 'shifts' | 'rotations' | 'assignments' | 'calendar' | 'guide' | 'schemes';
type Mode = 'simple' | 'advanced';

const TABS: readonly Tab[] = ['shifts', 'rotations', 'assignments', 'calendar', 'guide', 'schemes'];

interface Search {
  tab?: Tab;
  /**
   * Il settore e il caso scelti nella configurazione guidata.
   *
   * ⚠️ Nell'indirizzo, non in uno stato locale: chi trova il caso che descrive la sua azienda
   * lo manda a un collega, e il collega deve aprire la stessa pagina. Uno stato interno
   * renderebbe quel link una cosa da spiegare a voce.
   */
  sector?: string;
  useCase?: string;
  /** «Anche chi non ha una turnazione» acceso in Assegnazioni: è dove porta l'allarme. */
  missing?: true;
}

export const Route = createFileRoute('/_app/organization/work-shifts')({
  component: ShiftsPage,
  /* ⚠️ Senza `validateSearch` TanStack scarta il parametro: la scheda scelta non
     sopravviverebbe a un ricaricamento, né a un indirizzo mandato a un collega. */
  validateSearch: (s: Record<string, unknown>): Search => ({
    tab: TABS.includes(s.tab as Tab) ? (s.tab as Tab) : undefined,
    sector: typeof s.sector === 'string' && s.sector ? s.sector : undefined,
    useCase: typeof s.useCase === 'string' && s.useCase ? s.useCase : undefined,
    missing: s.missing === true || s.missing === 'true' || s.missing === 1 || s.missing === '1' ? true : undefined,
  }),
});

/* La semplificata: il calendario dove si lavora, e orari e schemi da tenere in ordine. */
const SIMPLE_BAR: MaeTabDef<Tab>[] = [
  { value: 'calendar', label: 'Calendario', icon: <IconCalendar size={15} /> },
  { value: 'schemes', label: 'Orari e schemi', icon: <IconClock size={15} /> },
];

/*
 * Dove finisce una scheda nell'altra modalità: `undefined` = c'è già, `null` = non ha un posto, e
 * allora la pagina si apre nella modalità che ce l'ha.
 */
const SIMPLE_EQUIVALENT: Partial<Record<Tab, Tab | null>> = {
  shifts: 'schemes', rotations: 'schemes', assignments: null, guide: null,
};
const ADVANCED_EQUIVALENT: Partial<Record<Tab, Tab | null>> = { schemes: 'shifts' };

const TABS_BAR: MaeTabDef<Tab>[] = [
  { value: 'shifts', label: 'Turni', icon: <IconClock size={15} /> },
  { value: 'rotations', label: 'Turnazioni', icon: <IconCalendarRepeat size={15} /> },
  { value: 'assignments', label: 'Assegnazioni', icon: <IconCalendarWeek size={15} /> },
  { value: 'calendar', label: 'Calendario turni', icon: <IconCalendar size={15} /> },
  { value: 'guide', label: 'Configurazione guidata', icon: <IconWand size={15} /> },
];

function ShiftsPage() {
  const { tab, sector, useCase, missing } = Route.useSearch();
  const navigate = Route.useNavigate();
  const mode = useUserPreference<Mode>(STORAGE_KEYS.shiftsMode, 'simple');
  /*
   * ⚠️ UN INDIRIZZO CHE NOMINA UNA SCHEDA LA APRE, in qualunque modalità ci si trovi.
   *
   * Le schede di una modalità hanno quasi sempre un posto nell'altra («Turni» e «Turnazioni» stanno
   * in «Orari e schemi»), e lì si va. «Assegnazioni» e la guida invece esistono solo in avanzata:
   * chi ci arriva da un link — l'allarme di chi non ha una turnazione, un collega — le vede in
   * avanzata **per questa visita**, senza che la sua preferenza cambi. Fino al 12-10-2026 si apriva
   * la prima scheda della modalità corrente: l'allarme portava sul calendario, dove le persone che
   * lo accendevano non si vedevano.
   */
  const other = (m: Mode): Mode => (m === 'simple' ? 'advanced' : 'simple');
  const equivalentIn = (m: Mode, t: Tab) => (m === 'simple' ? SIMPLE_EQUIVALENT : ADVANCED_EQUIVALENT)[t];
  /* La modalità di questa visita, decisa all'arrivo: cambiando scheda dentro l'avanzata aperta da
     un link non si deve tornare di colpo alla semplificata. */
  const [visitMode, setVisitMode] = useState<Mode | null>(
    () => (tab && equivalentIn(mode.value, tab) === null ? other(mode.value) : null),
  );
  /* Lo stesso quando l'indirizzo cambia con la pagina già aperta: l'allarme del menu si può
     premere mentre si è qui. Si aggiusta durante il disegno, non in un effetto, per non mostrare
     per un attimo la scheda sbagliata. */
  const [seenTab, setSeenTab] = useState(tab);
  if (tab !== seenTab) {
    setSeenTab(tab);
    const current = visitMode ?? mode.value;
    if (tab && equivalentIn(current, tab) === null) setVisitMode(other(current));
  }
  const effectiveMode: Mode = visitMode ?? mode.value;
  const bar = effectiveMode === 'simple' ? SIMPLE_BAR : TABS_BAR;
  const mapped = tab ? equivalentIn(effectiveMode, tab) : undefined;
  const active: Tab = mapped ?? (tab && bar.some((b) => b.value === tab) ? tab : bar[0].value);
  /* Cambiare modalità a mano chiude la visita: vale di nuovo la preferenza, e la scheda esce
     dall'indirizzo perché potrebbe non esistere nell'altra. */
  const changeMode = (v: Mode) => {
    mode.write(v);
    setVisitMode(null);
    if (tab) void navigate({ search: (s) => ({ ...s, tab: undefined, missing: undefined }), replace: true });
  };

  /*
   * ⚠️ RILEGGE **TUTTO**, invece di un elenco di chiavi.
   *
   * Le quattro schede leggono una dozzina di chiavi diverse — turni, cicli, assegnazioni, sedi,
   * dipendenti, il mese del calendario — e restano montate insieme. Un bottone che ne nominasse
   * un sottoinsieme sarebbe da riallineare a ogni query nuova, e quando qualcuno se lo
   * dimenticasse non darebbe errore: direbbe «aggiornato» lasciando indietro una scheda. Un
   * pulsante di aggiornamento che aggiorna quasi tutto è peggio di nessun pulsante, perché
   * toglie il dubbio invece di risolverlo.
   *
   * `invalidateQueries()` senza filtro segna scaduto tutto e **rilegge solo ciò che è in
   * pagina**: le altre schermate si rinfrescano quando le si riapre, che è ciò che ci si aspetta
   * dopo aver premuto «Aggiorna».
   */
  const qc = useQueryClient();
  const inLettura = useIsFetching() > 0;

  /*
   * ⚠️ IL PERCHÉ DELL'ALLARME, DENTRO LA PAGINA.
   *
   * Sul menu c'è solo un triangolo con un numero: chi lo segue arriva qui e deve leggere **cosa**
   * conta quel numero e come si spegne, altrimenti è un segnale senza spiegazione. Il conto è lo
   * stesso del menu e della home (`rotationCoverage.ts`), e vale per le stesse persone.
   */
  const withoutRotation = useMissingRotations();


  /*
   * Le schede già viste. Un `Set` in un ref e non nello stato: cambia *insieme* al render che
   * lo usa, e metterlo nello stato vorrebbe dire un secondo giro per montare il pannello —
   * cioè un lampeggio a ogni primo ingresso in una scheda.
   */
  const seen = useRef<Set<Tab>>(new Set([active]));
  seen.current.add(active);
  const [, force] = useState(0);
  useEffect(() => { force((n) => n + 1); }, [active]);

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconClock size={22} />}
        title="Turni di lavoro"
        tone="bordeaux"
        /* ⚠️ Dice **cosa si fa qui**, in una riga. Quella di prima — «prima cosa esiste, poi cosa
           ne esce, e fra le due c’è genera» — era una spiegazione del modello, non del lavoro:
           chi la leggeva doveva ricostruirsi da solo di cosa stesse parlando. */
        sub={effectiveMode === 'simple'
          ? 'Chi lavora, quando: lo scrivi sul calendario. Orari e schemi che si ripetono stanno nella seconda scheda.'
          : 'Qui definisci i turni e le turnazioni in vigore nella tua azienda, e chi le segue.'}
        actions={(
          <Group gap={8} wrap="wrap">
            {/* Due bottoni affiancati e non un interruttore: si vede subito in quale si è. */}
            <SegmentedControl
              aria-label="Modalità della pagina"
              size="xs"
              value={effectiveMode}
              onChange={(v) => changeMode(v as Mode)}
              data={[
                { value: 'simple', label: 'Semplificata' },
                { value: 'advanced', label: 'Avanzata' },
              ]}
            />
            {/* Sulla guida no: è contenuto fermo, non legge niente dal server. Un bottone che
                gira senza ricaricare nulla insegna a non fidarsi di quello delle altre schede. */}
            {active !== 'guide' && (
              <Button
                variant="default"
                leftSection={<IconRefresh size={15} />}
                loading={inLettura}
                onClick={() => void qc.invalidateQueries()}
              >
                Aggiorna
              </Button>
            )}
          </Group>
        )}
      />

      {withoutRotation.count > 0 && (
        <MaeAdvice
          tone="danger"
          title={withoutRotation.count === 1 ? 'Una persona non ha una turnazione' : `${withoutRotation.count} persone non hanno una turnazione`}
          action={{
            label: withoutRotation.count === 1 ? 'Mostra chi è' : 'Mostra chi sono',
            onClick: () => void navigate({ search: (v) => ({ ...v, tab: 'assignments', missing: true }), replace: true }),
          }}
        >
          Senza una turnazione per loro non nasce nessun turno: nel calendario restano vuote, e lo si
          scopre solo quando manca qualcuno. È il numero rosso accanto a «Turni di lavoro» nel menu, e
          si spegne quando ognuna segue un ciclo — assegnato da «Assegnazioni», o con «Fallo ripetere»
          sul calendario.
        </MaeAdvice>
      )}

      <MaeTabs
        value={active}
        onChange={(v) => void navigate({ search: (s) => ({ ...s, tab: v }), replace: true })}
        tabs={bar}
        mb={10}
      />

      {TABS.filter((s) => seen.current.has(s)).map((s) => (
        <div key={s} style={{ display: s === active ? undefined : 'none' }}>
          {s === 'shifts' && <ShiftsPanel />}
          {s === 'rotations' && <RotationsPanel />}
          {s === 'assignments' && <RotationAssignmentsPanel showMissing={missing} />}
          {s === 'calendar' && (effectiveMode === 'simple' ? <SimpleCalendarPanel /> : <ShiftCalendarPanel />)}
          {s === 'schemes' && (
            <Stack gap="lg">
              <ShiftsPanel />
              <RotationsPanel />
            </Stack>
          )}
          {s === 'guide' && (
            <ShiftGuidePanel
              sectorId={sector}
              caseId={useCase}
              onChoose={(set, cas) => void navigate({
                search: (v) => ({ ...v, sector: set ?? undefined, useCase: cas ?? undefined }),
                replace: true,
              })}
              /* Il passo porta dove si compie: si cambia scheda, e la scelta della guida resta
                 nell'indirizzo — tornando indietro si ritrova il caso dov'era. */
              onGoTo={(scheda) => void navigate({ search: (v) => ({ ...v, tab: scheda }), replace: true })}
            />
          )}
        </div>
      ))}
    </MaeShell>
  );
}
